import { DateTime } from 'luxon';

export const formatDateForAPI = (date: Date): string => DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');

export const formatDateForChart = (date: Date | string | number): Date => {
  let dateObj: Date;

  if (typeof date === 'number') {
    dateObj = new Date(date);
  } else if (typeof date === 'string') {
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }

  return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
};

/**
 * Parses a time string in "HH:mm" format to minutes since midnight
 */
const parseTimeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Checks if a given consumption date is tomorrow.
 * Uses local time for comparison.
 *
 * @param consumptionDate - The consumption date to check
 * @returns true if the date is tomorrow, false otherwise
 */
export const checkIfConsumptionDateNextDay = (consumptionDate: Date | string): boolean => {
  const consumptionDateTime = (
    typeof consumptionDate === 'string' ? DateTime.fromISO(consumptionDate) : DateTime.fromJSDate(consumptionDate)
  ).startOf('day');
  const todayPlusOne = DateTime.local().plus({ days: 1 }).startOf('day');
  return consumptionDateTime.equals(todayPlusOne);
};

/**
 * Checks if the current time is within the pricing index schedule for editing purposes.
 *
 * @param consumptionDate - The date of the consumption record to check if it's tomorrow
 * @param timeLimitLower - Lower time limit in "HH:mm" format (e.g., "00:00")
 * @param timeLimitUpper - Upper time limit in "HH:mm" format (e.g., "17:00")
 * @returns true if the hour is within range, false otherwise
 */
export const canEditByPricingIndexSchedule = (
  consumptionDate: Date | string,
  timeLimitLower: string,
  timeLimitUpper: string
): boolean => {
  // If the consumption date is not tomorrow, we consider it within hours for editing purposes
  if (!checkIfConsumptionDateNextDay(consumptionDate)) {
    return true;
  }

  const date = new Date();
  // Convert to DateTime in local timezone
  const dateTime = typeof date === 'string' ? DateTime.fromISO(date) : DateTime.fromJSDate(date);
  const localDateTime = dateTime.toLocal();

  // Get current time in minutes since midnight
  const currentMinutes = localDateTime.hour * 60 + localDateTime.minute;

  // Parse time limits to minutes
  const lowerMinutes = parseTimeToMinutes(timeLimitLower);
  const upperMinutes = parseTimeToMinutes(timeLimitUpper);

  // Allow editing if the consumption date is tomorrow and current time is within the pricing index hours
  return (
    checkIfConsumptionDateNextDay(consumptionDate) && currentMinutes >= lowerMinutes && currentMinutes <= upperMinutes
  );
};
